import React from 'react';
import {
  BooleanField,
  BooleanInput,
  Create,
  Filter,
  ReferenceField,
  ReferenceInput,
  required,
  SearchInput,
  SelectInput,
  TextField,
  TextInput,
  useRefresh,
  useShowController,
  Show,
} from 'react-admin';
import GravatarField from '../GravatarField';
import { ZippityDatagrid, ZippitySimpleForm } from '../../components';
import ScheduleChipWrapper from '../scheduled_work/ScheduleChipWrapper';
// eslint-disable-next-line import/no-cycle
import CustomerDetails from './CustomerDetails';
import ReferenceChild from '../../components/ReferenceChild';
import {
  getZ3PConfigValue,
  perPage,
  shouldShowImportButton,
} from '../../helper';
import BulkDeleteButton from '../../components/BulkDeleteButton';
import ListWithCSVImport from '../../components/ListWithCSVImport';
import CustomTitle from '../../components/CustomTitle';

const serviceLocations = getZ3PConfigValue('service_locations');
const hasWorkplaceServices = serviceLocations?.includes('workplace');

export const CustomerFilter = (props) => (
  <Filter {...props}>
    <SearchInput source="q" alwaysOn />
    {hasWorkplaceServices && (
      <ReferenceInput
        source="default_client_location_id"
        reference="client-location"
        sort={{ field: 'short_name', order: 'ASC' }}
        perPage={perPage}
      >
        <ReferenceChild
          {...props}
          optionText="short_name"
          source="default_client_location_id"
        />
      </ReferenceInput>
    )}
  </Filter>
);

const isSendingWelcomeEmail = getZ3PConfigValue('is_sending_welcome_email');

export const CustomerList = (props) => {
  // If the user added "?showImportButton=true" to the URL, then show the import button.
  const showImportButton = shouldShowImportButton(props);
  const showFleetFields = getZ3PConfigValue('enable_fleet_in_admin_portal');

  return (
    <ListWithCSVImport
      {...props}
      filters={<CustomerFilter />}
      sort={{ field: 'customer_id', order: 'DESC' }}
      perPage={50}
      bulkActionButtons={showImportButton ? <BulkDeleteButton /> : false}
      transformRows={(rows) =>
        rows.map((row) => ({ ...row, send_welcome_email: false }))
      }
    >
      <ZippityDatagrid rowClick="show">
        <TextField source="created_at" />
        <TextField source="modified_at" />
        <TextField source="customer_id" />
        <TextField source="email" />
        <TextField source="first_name" />
        <TextField source="last_name" />
        <TextField source="phone" />
        {hasWorkplaceServices ? (
          <ReferenceField
            source="default_client_location_id"
            reference="client-location"
            link="show"
            sortable={false}
          >
            <TextField source="short_name" />
          </ReferenceField>
        ) : (
          []
        )}
        <ScheduleChipWrapper
          source="upcoming_schedule_dates"
          sortable={false}
        />
        <TextField source="stripe_customer_id" />
        <TextField source="stripe_last4" />
        <TextField source="stripe_brand" />
        <TextField source="square_customer_id" />
        <TextField source="square_last4" />
        <TextField source="square_brand" />
        <GravatarField source="gravatar_image_url" sortable={false} />
        <TextField source="how_did_hear" sortable={false} />
        <BooleanField source="is_opted_out_of_carfax_reporting" />
        <BooleanField source="notifications_via_email" />
        <BooleanField source="notifications_via_text" />
        <TextField source="generated_referral_code" />
        <TextField source="custom_referral_code" />
        {showFleetFields ? <BooleanField source="is_fleet_user" /> : <></>}
        {showFleetFields ? <TextField source="fleet_account_name" /> : <></>}
      </ZippityDatagrid>
    </ListWithCSVImport>
  );
};

export const CustomerShow = (props) => {
  const refresh = useRefresh();
  const { loading, record } = useShowController(props);
  if (loading) {
    return 'Loading...';
  }
  const { history } = props;
  if (history.location.pathname.includes('/redirect')) {
    const showPath = history.location.pathname.substr(
      0,
      history.location.pathname.lastIndexOf('/'),
    );
    history.replace(showPath);
    refresh();
  }
  return (
    <Show
      {...props}
      title={<CustomTitle title="resources.customer.title" />}
      component="div"
    >
      <CustomerDetails record={record} {...props} />
    </Show>
  );
};

export const leadSourceOptions = [
  { id: 'friend', name: 'From a friend' },
  { id: 'employer', name: 'From Employer' },
  { id: 'property_manager', name: 'From the property manager' },
  { id: 'garage', name: 'Saw the Service Garage' },
  { id: 'kiosk', name: 'Saw the Kiosk' },
  { id: 'signs', name: 'Saw signs' },
  { id: 'facebook', name: 'Facebook' },
  { id: 'tabling', name: 'Tabling Event' },
  { id: 'online_ads', name: 'Online Ads' },
  { id: 'vehicle_flyer', name: 'Received Vehicle Flyer' },
  { id: 'search_engine', name: 'Search Engine' },
  { id: 'company_website', name: 'Company Website' },
  { id: 'social_media', name: 'Social Media' },
  { id: 'service_shop', name: 'Service Shop Branch' },
  { id: 'property_manager_or_employer', name: 'Property Manager or Employer' },
  { id: 'hulu', name: 'Hulu' },
  { id: 'ad_flyer_or_mailer', name: 'From an ad, flyer, or mailer' },
  { id: 'other', name: 'Other' },
];

export const CustomerCreate = (props) => {
  const showFleetFields = getZ3PConfigValue('enable_fleet_in_admin_portal');

  return (
    <div>
      <Create {...props}>
        <ZippitySimpleForm redirect="show">
          <TextInput source="email" validate={[required()]} />
          <TextInput source="first_name" validate={[required()]} />
          <TextInput source="last_name" />
          <TextInput source="phone" validate={[required()]} />
          {hasWorkplaceServices && (
            <ReferenceInput
              source="default_client_location_id"
              reference="client-location"
              perPage={perPage}
            >
              <ReferenceChild
                {...props}
                optionText="long_name"
                source="default_client_location_id"
              />
            </ReferenceInput>
          )}
          <SelectInput source="how_did_hear" choices={leadSourceOptions} />
          <BooleanInput source="notifications_via_email" />
          <BooleanInput source="notifications_via_text" />
          <BooleanInput source="is_opted_out_of_carfax_reporting" />
          {showFleetFields && (
            <>
              <BooleanInput source="is_fleet_user" />
              <TextInput source="fleet_account_name" />
            </>
          )}
          {isSendingWelcomeEmail && (
            <p>
              NOTE: When you click Save, the customer will receive a Welcome
              email and will be able to set a password.
            </p>
          )}
        </ZippitySimpleForm>
      </Create>
    </div>
  );
};

export const printCustomer = (record) => {
  if (!record?.id) {
    return '';
  }
  const { first_name, last_name, email, fleet_account_name } = record;
  if (fleet_account_name) {
    return `${fleet_account_name} - ${first_name} ${last_name} (${email})`;
  }
  return `${first_name} ${last_name} (${email})`;
};
